import type { NetworkError } from '@apollo/client/errors';
import type { GraphQLFormattedError } from 'graphql';

import { EventCode } from '@/enums/events-enum';
import { ApolloClient, ApolloLink, HttpLink, from, InMemoryCache } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { loadState } from '@/store/localStorage';

export const enum GRAPHQL_SERVERS {
  WORDPRESS = 'wordpress',
  WEBAPP = 'webapp',
}

const wordpressGraphqlUri = process.env.REACT_APP_PRODUCT_WORDPRESS_GRAPHQL_URI;
if (!wordpressGraphqlUri) {
  throw new Error('REACT_APP_PRODUCT_WORDPRESS_GRAPHQL_URI is not defined');
}

const graphqGatewaylUri = process.env.REACT_APP_PRODUCT_GRAPHQL_GATEWAY_URI;
if (!graphqGatewaylUri) {
  throw new Error('REACT_APP_PRODUCT_GRAPHQL_GATEWAY_URI is not defined');
}

const directionalLink = new RetryLink().split(
  (operation) => {
    const { server } = operation.getContext();

    return server === GRAPHQL_SERVERS.WEBAPP;
  },
  new HttpLink({ uri: graphqGatewaylUri }),
  new HttpLink({ uri: wordpressGraphqlUri })
);

const authMiddleware = new ApolloLink((operation, forward) => {
  const token = loadState('authToken');

  operation.setContext((context: any) => ({
    ...context,
    headers: {
      ...context.headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  }));

  return forward(operation);
});

// Log any GraphQL errors or network error that occurred
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach((error) => {
      console.log(
        `[GraphQL error]: Message: ${error.message}, Location: ${error.locations}, Path: ${
          error.path
        }, Extensions: ${JSON.stringify(error.extensions)}`
      );
      window.dispatchEvent(
        new CustomEvent<GraphQLFormattedError>(EventCode.GRAPHQL_ERROR_EVENT, { detail: error })
      );
    });
  }
  if (networkError) {
    console.log(`[Network error]: ${networkError}`);
    window.dispatchEvent(
      new CustomEvent<NetworkError>(EventCode.NETWORK_ERROR_EVENT, { detail: networkError })
    );
  }
});

export const graphqlClient = new ApolloClient({
  link: from([errorLink, authMiddleware, directionalLink]),
  cache: new InMemoryCache(),
  connectToDevTools: process.env.NODE_ENV !== 'production',
});
